import {
  CustomReward,
  CustomRewardBalance,
  CustomRewardLog,
  CustomRewardLogStats,
  CustomRewardLogType,
  CustomRewardParams,
} from "@joonapp/web-shared"

import { InventoryItem, Shop, ShopItem } from "../../types"
import {
  CustomRewardTemplate,
  OnboardingCustomRewardTemplate,
  ScreenTimeLog,
  ScreenTimeManualException,
  ScreenTimeScheduledException,
} from "../../types/rewards"
import { createJoonAPIClient } from "../../util/joon-api"
import { groupRewardTemplatesById } from "../../util/util"

export const getCustomRewards = async () => {
  const API = createJoonAPIClient()
  const response = await API.get<{ results: CustomReward[] }>(
    "api/custom-rewards/"
  )
  return response.data.results
}

export const createCustomReward = async (rewardInfo: CustomRewardParams) => {
  const API = createJoonAPIClient()
  return API.post("api/custom-rewards/", rewardInfo).then(
    (response) => response.data
  )
}

export const editCustomReward = async ({
  rewardInfo,
  rewardId,
}: {
  rewardInfo: CustomRewardParams
  rewardId: number
}) => {
  const API = createJoonAPIClient()
  return API.patch(`api/custom-rewards/${rewardId}/`, rewardInfo)
}

export const getRewardTemplates = async () => {
  const API = createJoonAPIClient()
  return API.get<{ results: CustomRewardTemplate[] }>(
    "api/custom-rewards/templates/"
  ).then((response) => response.data.results)
}

export const getOnboardingRewardTemplates = async () => {
  const API = createJoonAPIClient()
  const response = await API.get<{
    templates: OnboardingCustomRewardTemplate[]
  }>("api/custom-rewards/onboarding-templates/")
  return groupRewardTemplatesById(response.data.templates)
}

export const purchaseCustomReward = async ({
  rewardId,
  childId,
  amount = 1,
}: {
  rewardId: number
  childId: number
  amount?: number
}) => {
  const API = createJoonAPIClient()
  return API.post(`api/custom-rewards/${rewardId}/purchase/`, {
    user_id: childId,
    amount,
  })
}

export const redeemCustomReward = async ({
  rewardId,
  amount,
  userId,
  extra_action = "none",
}: {
  rewardId: number
  amount: number
  userId: number
  extra_action?: "none" | "start_active_session" | "auto_deduct"
}) => {
  const API = createJoonAPIClient()
  return API.post(`api/custom-rewards/${rewardId}/redeem/`, {
    amount,
    user_id: userId,
    extra_action,
  })
}

export const deleteCustomReward = async (rewardId: number) => {
  const API = createJoonAPIClient()
  return API.delete(`api/custom-rewards/${rewardId}/`)
}

export const getCustomRewardLogs = async ({
  userId,
  rewardId,
  minDate,
  maxDate,
  type,
}: {
  userId?: number
  rewardId?: number
  minDate?: string
  maxDate?: string
  type?: CustomRewardLogType
}) => {
  const API = createJoonAPIClient()
  return API.get<{ results: CustomRewardLog[] }>("api/custom-reward-logs/", {
    params: {
      user_id: userId,
      reward_id: rewardId,
      min_date: minDate,
      max_date: maxDate,
      type,
    },
  }).then((response) => response.data.results)
}

export const undoCustomRewardLog = async (logId: number) => {
  const API = createJoonAPIClient()
  return API.post(`api/custom-reward-logs/${logId}/undo/`)
}

export const getCustomRewardLogsStats = async ({
  rewardId,
}: {
  rewardId?: number
}) => {
  const API = createJoonAPIClient()
  return API.get<CustomRewardLogStats>("api/custom-reward-logs/stats/", {
    params: { reward_id: rewardId },
  }).then((response) => response.data)
}

export const getCustomRewardBalances = async () => {
  const API = createJoonAPIClient()
  return API.get<{ balances: CustomRewardBalance[] }>(
    "api/custom-rewards/balances/"
  ).then((response) => response.data.balances)
}

export const getScreenTimeBalances = async () => {
  const API = createJoonAPIClient()
  return API.get<{ screen_time_balances: CustomRewardBalance[] }>(
    "api/custom-rewards/balances/"
  ).then((response) => response.data.screen_time_balances)
}

export const getParentShop = async ({
  userId,
  shopType,
}: {
  userId: number
  shopType: string
}): Promise<Shop> => {
  const API = createJoonAPIClient()
  return API.get<{ results: Shop[] }>("api/shops/", {
    params: { user_id: userId, identifier: shopType },
  }).then((response) => response.data.results[0])
}

export const purchaseShopItem = async ({
  shopItem,
  userId,
}: {
  shopItem: ShopItem
  userId: number
}): Promise<InventoryItem> => {
  const API = createJoonAPIClient()
  return API.post(`api/shop-items/${shopItem.id}/purchase/`, {
    user_id: userId,
    amount: 1,
  }).then((response) => response.data.inventory_items?.[0])
}

export const updateChildAssignment = async ({
  assignmentId,
  bypassRedeem,
}: {
  assignmentId: number
  bypassRedeem: boolean
}) => {
  const API = createJoonAPIClient()
  return API.patch(`api/custom-reward-assignments/${assignmentId}/`, {
    bypass_redeem: bypassRedeem,
  })
}

export const getScreenTimeScheduledExceptions = async ({
  userId,
}: {
  userId: number | null
}) => {
  if (!userId) return []
  const API = createJoonAPIClient()
  return API.get<{ results: ScreenTimeScheduledException[] }>(
    "api/screen-time-exception-schedules/",
    { params: { user_id: userId } }
  ).then((response) => response.data.results)
}

export const createScreenTimeScheduledException = async ({
  userId,
  weekday,
  start_time,
  end_time,
}: {
  userId: number
  weekday: string
  start_time: string
  end_time: string
}) => {
  const API = createJoonAPIClient()
  return API.post("api/screen-time-exception-schedules/", {
    user_id: userId,
    weekday,
    start_time,
    end_time,
  })
}

export const updateScreenTimeScheduledException = async ({
  exceptionId,
  start_time,
  end_time,
}: {
  exceptionId: number
  start_time: string
  end_time: string
}) => {
  const API = createJoonAPIClient()
  return API.patch(`api/screen-time-exception-schedules/${exceptionId}/`, {
    start_time,
    end_time,
  })
}

export const deleteScreenTimeScheduledException = async (
  exceptionId: number
) => {
  const API = createJoonAPIClient()
  return API.delete(`api/screen-time-exception-schedules/${exceptionId}/`)
}

export const getScreenTimeManualException = async ({
  userId,
}: {
  userId: number | null
}) => {
  if (!userId) return null
  const API = createJoonAPIClient()
  return API.get<ScreenTimeManualException | null>(
    "api/screen-time-exception-schedules/manual-exception/",
    { params: { user_id: userId } }
  ).then((response) => response.data)
}

export const createScreenTimeManualException = async ({
  userId,
  end_datetime,
}: {
  userId: number
  end_datetime: string
}) => {
  const API = createJoonAPIClient()
  return API.post("api/screen-time-exception-schedules/manual-exception/", {
    user_id: userId,
    end_datetime,
  })
}

export const deleteScreenTimeManualException = async (userId: number) => {
  const API = createJoonAPIClient()
  return API.delete(`api/screen-time-exception-schedules/manual-exception/`, {
    params: { user_id: userId },
  })
}

// used to know if the user is in an exception (either scheduled or manual)
export const getScreenTimeCurrentException = async ({
  userId,
}: {
  userId: number | null
}) => {
  if (!userId) return null
  const API = createJoonAPIClient()
  return API.get<{ current_exception_end_datetime: string | null }>(
    `api/screen-time-exception-schedules/current-exception/`,
    {
      params: { user_id: userId },
    }
  ).then((response) => response.data.current_exception_end_datetime)
}

export const getScreenTimeActiveSession = async ({
  userId,
}: {
  userId: number
}) => {
  const API = createJoonAPIClient()
  return API.get<ScreenTimeLog | null>("api/screen-time-logs/active-session/", {
    params: { user_id: userId },
  }).then((response) => response.data)
}

export const endScreenTimeSession = async ({
  sessionId,
}: {
  sessionId: number
}) => {
  const API = createJoonAPIClient()
  return API.post(`api/screen-time-logs/${sessionId}/end-session/`, {
    return_unused_balance: true,
  })
}
